import { keepPreviousData, useQuery } from '@tanstack/react-query';
import useGlynt from '../useGlynt';
import { PackageEntryStatus, PaginatedPackageEntriesResponse } from '../../../types/packager-types';

interface IUseGetPackageEntriesParams {
  packageId?: string;
  status?: PackageEntryStatus;
  limit?: number;
  offset?: number;
  documentId?: string;
  // A completeness drill-down's raw query-param dict, forwarded verbatim
  // (e.g. { package, document, status_in_package, missing_extraction }). The API
  // owns "which documents" — the client never re-derives the filter. `advanced`
  // is supplied by the hook, not the dict.
  drilldownParams?: Record<string, string>;
}

const useGetPackageEntries = ({
  packageId,
  status,
  documentId,
  limit = 10,
  offset = 0,
  drilldownParams,
}: IUseGetPackageEntriesParams) => {
  const glynt = useGlynt();

  return useQuery({
    queryKey: [
      'packageEntries',
      packageId,
      status,
      limit,
      offset,
      documentId,
      drilldownParams ? JSON.stringify(drilldownParams) : undefined,
    ],

    queryFn: async () => {
      return (await glynt).packages.packageEntries({
        advanced: true,
        package: packageId,
        status_in_package: status,
        document: documentId,
        // Drill-down dict wins over the named filters when present.
        ...drilldownParams,
        limit,
        offset,
      });
    },

    refetchOnWindowFocus: false,
    enabled: !!packageId,
    placeholderData: keepPreviousData
  });
};

export default useGetPackageEntries;
